import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { driver, Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour.css';
import { tourSteps, tourConfig } from './tourSteps';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/auth';

interface TourContextType {
  startTour: () => void;
  isTourActive: boolean;
  hasCompletedTour: boolean;
  markTourAsCompleted: () => void;
  resetTour: () => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

const TOUR_COMPLETED_KEY = 'cashflow_tour_completed';
const TOUR_SKIPPED_KEY = 'cashflow_tour_skipped';

interface TourProviderProps {
  children: React.ReactNode;
}

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [isTourActive, setIsTourActive] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(() => {
    // Check user object first, fallback to localStorage for backwards compatibility
    return user?.tour_completed ?? localStorage.getItem(TOUR_COMPLETED_KEY) === 'true';
  });

  const tourCompleted = useCallback(async () => {
    try {
      await authService.completeTour();
    } catch {
      localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
    }
    setHasCompletedTour(true);
  }, []);

  // Update hasCompletedTour when user changes
  useEffect(() => {
    if (user?.tour_completed !== undefined) {
      setHasCompletedTour(user.tour_completed);
    }
  }, [user?.tour_completed]);

  const startTour = useCallback(() => {
    // Declare driverObj so handleKeyPress can reference it
    let driverObj: Driver;

    // Add keyboard event listener for Enter key
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && driverObj.isActive()) {
        event.preventDefault();
        event.stopPropagation();

        if (driverObj.isLastStep()) {
          driverObj.destroy();
        } else {
          driverObj.moveNext();
        }
      }
    };

    driverObj = driver({
      ...tourConfig,
      onDestroyed: () => {
        setIsTourActive(false);
        document.removeEventListener('keydown', handleKeyPress, true);
      },
      onCloseClick: () => {
        tourCompleted();
        driverObj.destroy();
      },
      onNextClick: () => {
        if (driverObj.isLastStep()) {
          tourCompleted();
          driverObj.destroy();
        } else {
          driverObj.moveNext();
        }
      },
      onHighlightStarted: () => {
        setTimeout(() => {
          if (driverObj.isLastStep()) {
            const nextBtn = document.querySelector('.driver-popover-next-btn');
            if (nextBtn) {
              nextBtn.textContent = 'Done ✓';
            }
          }
        }, 0);
      },
    });

    document.addEventListener('keydown', handleKeyPress, true);

    setIsTourActive(true);
    driverObj.setSteps(tourSteps);
    driverObj.drive();
  }, [tourCompleted]);

  const markTourAsCompleted = tourCompleted;

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_COMPLETED_KEY);
    localStorage.removeItem(TOUR_SKIPPED_KEY);
    setHasCompletedTour(false);
  }, []);

  const value: TourContextType = {
    startTour,
    isTourActive,
    hasCompletedTour,
    markTourAsCompleted,
    resetTour,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

/**
 * Hook to access tour functionality
 */
export const useTour = (): TourContextType => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};
